import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { requireAppCheck } from '@jfw/angular/shared/app-check';
import { PLATFORM_DP_ENVIRONMENT } from '@jfw/angular/shared/tokens';
import {
  AddOrderTextLine,
  ChangeEventDatesRequest,
  MovedOrder,
  MoveOrderDateRequest,
  NationalEvent,
  OrderComparisonResult,
  SaveOrderNotes,
  WholesaleOrder,
  WholesaleOrderDelete,
} from '@jfw/types';
import { catchError, Observable, of, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class WholesaleOrderService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(PLATFORM_DP_ENVIRONMENT);

  private readonly reqHeader = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  public placeOrderV2(order: WholesaleOrder): Observable<WholesaleOrder> {
    return this.http
      .post<WholesaleOrder>(
        `${this.environment.platform_order_api_url}/wholesale-order`,
        order,
        {
          ...requireAppCheck,
          headers: this.reqHeader,
        },
      )
      .pipe(
        catchError((error) => {
          if (error.status === 422 && error.error) {
            // The API sends back the WholesaleOrder with errors populated on a 422
            return of(error.error as WholesaleOrder);
          }
          console.error('Error in placeOrderV2:', error);
          // Construct a fallback error response
          return of({
            ...order,
            errors: [error.message || 'An unexpected error occurred'],
          } as WholesaleOrder);
        }),
      );
  }

  /**
   * EXT-6321: moves one order that isn't on a national event. A blocked move
   * arrives as an HttpErrorResponse (422, or 403 for another account's order)
   * whose `error` is an EventDateChangeBlockedReason -- left for the caller to
   * read rather than swallowed here.
   */
  public moveOrderDate(request: MoveOrderDateRequest): Observable<MovedOrder> {
    return this.http.post<MovedOrder>(
      `${this.environment.platform_order_api_url}/move-order-date`,
      request,
      {
        ...requireAppCheck,
        headers: this.reqHeader,
      },
    );
  }

  /**
   * EXT-6321: the same event move Event Planner's change-dates makes, for
   * Order Entry's Change Date on an order that's on an event. Errors
   * propagate the same way as moveOrderDate above.
   *
   * EXT-6339: posts to Order Entry's own order/v1 route, so it needs the
   * Order Entry permission (WholesaleOrder.Edit), not Event Search's.
   */
  public changeEventDates(
    request: ChangeEventDatesRequest,
  ): Observable<NationalEvent> {
    return this.http.post<NationalEvent>(
      `${this.environment.platform_order_api_url}/change-event-dates`,
      request,
      {
        ...requireAppCheck,
        headers: this.reqHeader,
      },
    );
  }

  /**
   * EXT-5795v2: notes-only save on an order that already exists, hit by order-entry's debounced
   * autosave -- deliberately a separate call from placeOrderV2 so a notes-only edit never
   * reaches the legacy WCF CHANGEORDER call (which rejects with OP191 "nothing was changed"
   * whenever nothing it tracks actually differs). Fire-and-forget: this is a background autosave,
   * not a blocking part of the main submit flow, so a failure just logs rather than surfacing an
   * error to the user -- the note is still saved on the next successful autosave tick or the next
   * full order submission.
   */
  public saveOrderNotes(payload: SaveOrderNotes): Observable<void> {
    return this.http
      .post<void>(
        `${this.environment.platform_order_api_url}/wholesale-order-notes`,
        payload,
        {
          ...requireAppCheck,
          headers: this.reqHeader,
        },
      )
      .pipe(
        catchError((error) => {
          console.error('Error in saveOrderNotes:', error);
          return of(undefined);
        }),
      );
  }

  /**
   * EXT-5142: adds one additional shipping-instruction line to an order that already exists, hit
   * by order-entry's "+" affordance -- deliberately never swallows an error the way
   * saveOrderNotes does above, since that one is a background autosave while this is a direct,
   * user-initiated save the component needs to show success/failure for (including a 409 when
   * the order's packing slip has already been generated -- see the handler's own comment).
   */
  public addOrderTextLine(payload: AddOrderTextLine): Observable<void> {
    return this.http
      .post<void>(
        `${this.environment.platform_order_api_url}/wholesale-order-text-line`,
        payload,
        {
          ...requireAppCheck,
          headers: this.reqHeader,
        },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error in addOrderTextLine:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * EXT-5877: manually creates (or regenerates) an order's LNSEQ=1 shipping-instructions row --
   * the outside-the-EXT-5876-trigger-window case, hit by order-entry's "Add Shipping
   * Instructions" affordance. Same shape/error-handling as addOrderTextLine above (including the
   * 409 packing-slip-generated case); the two hit different backend routes since they target
   * different ORDTEXT LNSEQ values.
   */
  public setShippingInstructions(payload: AddOrderTextLine): Observable<void> {
    return this.http
      .post<void>(
        `${this.environment.platform_order_api_url}/wholesale-order-shipping-instructions`,
        payload,
        {
          ...requireAppCheck,
          headers: this.reqHeader,
        },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error in setShippingInstructions:', error);
          return throwError(() => error);
        }),
      );
  }

  /**
   * EXT-5689: dev-only comparison harness — never called from a real submission path, only from
   * order-entry's own "Compare (Dev Only)" pipeline (gated on environment.enableOrderComparisonHarness).
   * Hits the /order-native/v1 mount, which doesn't exist at all in prod (see native-order.routes.ts's
   * NODE_ENV gate) — deliberately no catchError fallback here, unlike placeOrderV2: this is a
   * diagnostic call, and swallowing its failure into a fake success shape would hide exactly the
   * information a dev tester is trying to see.
   */
  public compareWholesaleOrder(
    order: WholesaleOrder,
  ): Observable<OrderComparisonResult> {
    return this.http.post<OrderComparisonResult>(
      `${this.environment.platform_order_native_api_url}/compare-wholesale-order`,
      order,
      {
        ...requireAppCheck,
        headers: this.reqHeader,
      },
    );
  }

  /**
   * EXT-5987: whole-order delete/reactivate arm of the same dev-only harness. Separate from
   * compareWholesaleOrder above because the two exercise different native writers entirely --
   * that one runs placeOrderNative (NEWORDER/CHANGEORDER, including a *detail-line* delete),
   * this one runs deleteOrderNative (whole-order DELETEORDER/REACTIVATEORDER). Before this,
   * nothing reachable only through deleteOrderNative could be live-verified through Compare at
   * all. Same no-catchError reasoning as compareWholesaleOrder: a diagnostic call's failure
   * must stay visible.
   */
  public compareWholesaleOrderDelete(
    orderDelete: WholesaleOrderDelete,
    reactivate = false,
  ): Observable<OrderComparisonResult> {
    const path = reactivate
      ? 'compare-wholesale-order-reactivate'
      : 'compare-wholesale-order-delete';
    return this.http.post<OrderComparisonResult>(
      `${this.environment.platform_order_native_api_url}/${path}`,
      orderDelete,
      {
        ...requireAppCheck,
        headers: this.reqHeader,
      },
    );
  }
}
